import gsap from "gsap";
import { module as BaseModule } from "modujs";
import Swiper from "swiper";
import { EffectFade, Keyboard, Mousewheel, Navigation, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/keyboard";
import "swiper/css/navigation";

export default class extends BaseModule {
  init() {
    const slider = this.el
    const { sliderConfig } = slider.dataset
    this.slider = slider
    this.cursor = slider.querySelector('[data-module-slider-cursor]')
    this.supportsHoverCursor = window.matchMedia("(hover: hover) and (pointer: fine)").matches
    if (this.cursor && this.supportsHoverCursor) {
      gsap.set(this.cursor, { xPercent: -50, yPercent: -50 })
    }

    const params = {
      default: {
        modules: [EffectFade, Keyboard, Mousewheel, Pagination],
        keyboard: {
          enabled: true,
        },
        mousewheel: {
          enabled: true,
        },
        pagination: {
          el: '.swiper-pagination',
          type: "fraction",
        },
        allowTouchMove: false,
        simulateTouch: false,
        grabCursor: false,
        slidesPerView: 1,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        loop: true,
      },
    }

    this.swiper = new Swiper(slider, params[sliderConfig])
    this.getDirectionFromEvent = (event) => {
      if (!this.supportsHoverCursor || !event?.clientX) {
        return "next"
      }

      const bounds = slider.getBoundingClientRect()
      const center = bounds.left + bounds.width / 2

      return event.clientX < center ? "prev" : "next"
    }
    this.updateCursorPosition = (event) => {
      if (!this.cursor || !this.supportsHoverCursor || !event?.clientX || !event?.clientY) {
        return
      }

      gsap.to(this.cursor, {
        x: event.clientX,
        y: event.clientY,
        duration: 0.15,
        ease: "power2.out",
        overwrite: "auto",
      })
    }
    this.updateCursorDirection = (event) => {
      if (!this.cursor || !this.supportsHoverCursor) {
        return
      }

      const direction = this.getDirectionFromEvent(event)
      this.cursor.dataset.direction = direction
    }
    this.handlePointerEnter = (event) => {
      if (!this.cursor || !this.supportsHoverCursor) {
        return
      }

      if (event.pointerType === "touch") {
        return
      }

      this.slider.classList.add("has-slider-cursor")
      this.cursor.classList.add("is-visible")
      this.updateCursorDirection(event)
      this.updateCursorPosition(event)
    }
    this.handlePointerMove = (event) => {
      if (!this.cursor || !this.supportsHoverCursor) {
        return
      }

      if (event.pointerType === "touch") {
        return
      }

      this.updateCursorDirection(event)
      this.updateCursorPosition(event)
    }
    this.handlePointerLeave = () => {
      if (!this.cursor) {
        return
      }

      this.slider.classList.remove("has-slider-cursor")
      this.cursor.classList.remove("is-visible")
    }
    this.syncCursorOnInit = () => {
      if (!this.cursor || !this.supportsHoverCursor || !this.slider.matches(":hover")) {
        return
      }

      const bounds = this.slider.getBoundingClientRect()
      const fallbackEvent = {
        clientX: bounds.left + (bounds.width / 2),
        clientY: bounds.top + (bounds.height / 2),
      }

      this.slider.classList.add("has-slider-cursor")
      this.cursor.classList.add("is-visible")
      this.updateCursorDirection(fallbackEvent)
      this.updateCursorPosition(fallbackEvent)
    }
    this.handleNextOnTap = (event) => {
      if (
        event.target.closest('[data-module-slider-next]') ||
        event.target.closest('[data-module-slider-prev]') ||
        event.target.closest('.swiper-pagination')
      ) {
        return
      }

      if (event.type === "click") {
        const direction = this.getDirectionFromEvent(event)
        if (direction === "prev") {
          this.swiper.slidePrev()
          return
        }
      }

      this.swiper.slideNext()
    }
    slider.addEventListener("click", this.handleNextOnTap)
    slider.addEventListener("touchend", this.handleNextOnTap)
    slider.addEventListener("pointerenter", this.handlePointerEnter)
    slider.addEventListener("pointermove", this.handlePointerMove)
    slider.addEventListener("pointerleave", this.handlePointerLeave)
    slider.addEventListener("mouseenter", this.handlePointerEnter)
    slider.addEventListener("mousemove", this.handlePointerMove)
    slider.addEventListener("mouseleave", this.handlePointerLeave)
    this.syncCursorOnInit()

    gsap.to(slider, {
      opacity: 1,
      duration: 3,
      ease: "power2.Out",
    })
  }

  destroy() {
    if (this.handleNextOnTap) {
      this.el.removeEventListener("click", this.handleNextOnTap)
      this.el.removeEventListener("touchend", this.handleNextOnTap)
      this.el.removeEventListener("pointerenter", this.handlePointerEnter)
      this.el.removeEventListener("pointermove", this.handlePointerMove)
      this.el.removeEventListener("pointerleave", this.handlePointerLeave)
      this.el.removeEventListener("mouseenter", this.handlePointerEnter)
      this.el.removeEventListener("mousemove", this.handlePointerMove)
      this.el.removeEventListener("mouseleave", this.handlePointerLeave)
    }

    this.el.classList.remove("has-slider-cursor")
  }
}
